import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useGetItemsQuery } from '../../../slices/simpleApiSlice';
//иконки
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CircularProgress from '@mui/material/CircularProgress';
//компоненты
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Pagination,
  Typography,
} from '@mui/material';
import Alerton from '../../Alerton';
import ScreenContainer from '../../ScreenContainer';
import GridTableList from '../../GridTableList';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import FieldInput from '../UI/FieldInput/FieldInput';
import AddDrawer from '../../AddDrawer';

const normalizePath = (path) =>
  path.replace(/^\/+/, '/').replace(/([^:])\/{2,}/g, '$1/');

const ItemsViewer = ({
  rootUrl = 'api',
  section,
  slug,
  permissions,
  pagination,
  navigateOptions = {
    replace: false,
    state: {},
    backHashKey: '_id',
    itemKey: '_id',
  },
}) => {
  const { canRead = false, canCreate = false, canDelete = false } = permissions;
  const navigate = useNavigate();

  //идентификатор текущей строки
  //для автоскрола и подсветки в списке
  const location = useLocation();
  const fullHash = location.hash; //hash "#id"
  const anchorId = fullHash.slice(1); // "id"

  //номер страницы
  const [searchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1', 10);

  let queryUrl;
  let itemDir;
  if (section) {
    queryUrl = normalizePath(`/${rootUrl}/${section}/${slug}`);
    itemDir = normalizePath(`/${section}/${slug}`);
  } else {
    queryUrl = normalizePath(`/${rootUrl}/${slug}`);
    itemDir = normalizePath(`/${slug}`);
  }

  // console.log('queryUrl', queryUrl);
  // console.log('itemDir', itemDir);

  const directoryTag = `Directory-${slug.charAt(0).toUpperCase() + slug.slice(1)}`;
  const listTag = `${directoryTag}-List`;
  const [viewInvisible, setViewInvisible] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [searchParam, setSearchParam] = useState('');
  const [filterInputs, setFilterInputs] = useState({});
  const [addDrawerOptions, setAddDrawerOptions] = useState({ open: false });
  const { data, isLoading, isFetching, isError, error } = useGetItemsQuery(
    {
      url: queryUrl,
      params: {
        viewInvisible,
        ...pagination,
        page,
        searchParam,
      },
      providesTags: [listTag],
    },
    { skip: !canRead || !slug }, // ← Пропуск запроса);
  );

  console.log('ItemsViewer data', data);

  // Функция, которая запускает поиск
  const handleSearch = (search = true) => {
    if (search) {
      setSearchParam(searchValue.trim()); // ← здесь происходит refetch
    } else {
      setSearchValue('');
      setSearchParam('');
    }
  };

  useEffect(() => {
    // console.log('data', data);
  }, [data]);

  //динамические колонки списка
  const columns = useMemo(
    () =>
      data?.metadata?.columns || [
        { field: 'title', label: 'Название', width: 12 },
      ],
    [data?.metadata?.columns],
  ); // ← Мемоизация

  const [message, setMessage] = useState('');

  //навигация при клике на строку
  const handleRowSelect = (rowData) => {
    const {
      backHashKey = '_id',
      itemKey = '_id',
      state = {},
      replace = false,
    } = navigateOptions;

    // console.log('navigateOptions', navigateOptions);

    //по умолчанию берем идентификатор элемента  массива _id
    //и находим значение по ключу
    //но иногда нужно вместо идентификатора использовать какое то другое поле
    //например url и тогда нужно использовать rowData.url
    // console.log('rowData', rowData);
    const id = itemKey ? rowData[itemKey] : rowData._id;
    // console.log('handleRowSelect id', id);

    const backHash = backHashKey ? rowData[backHashKey] : rowData._id;
    const stateData = {
      ...state,
      replace,
      backUrl: window.location.pathname + window.location.search, // ✅ Текущий URL!
      backHash: `${backHash}`,
    };

    if (rowData?.url) {
      return navigate(rowData.url, { state: stateData });
    }

    navigate(`${itemDir}/${id}`, { state: stateData });
  };

  //автоскрол к строке по идентификатору из хеша
  useEffect(() => {
    const behaviors = [
      'auto', // По умолчанию: мгновенно ИЛИ плавно (зависит от CSS scroll-behavior)
      'smooth', // Плавная анимация скролла (рекомендуется)
      'instant', // Мгновенный прыжок без анимации (то же что auto в большинстве случаев)
    ];
    const blocks = [
      'start', // Верх элемента к верху viewport (или container)
      'center', // Центр элемента в центр viewport
      'end', // Низ элемента к низу viewport
      'nearest', // Минимальный скролл: элемент полностью видим (лучший для списков!)
    ];
    if (anchorId) {
      setTimeout(() => {
        const element = document.querySelector(`[id='${anchorId}']`);
        element?.scrollIntoView({ behavior: behaviors[2], block: blocks[1] });
      }, 100);
    }
  }, [anchorId]);

  let errorMessage = '';
  if (isLoading || isFetching) {
    return <CircularProgress sx={{ display: 'block', mx: 'auto' }} />;
  } else if (isError) {
    errorMessage = error?.data?.message || error?.error || error?.message;
  }

  if (!canRead) {
    errorMessage = 'Недостаточно прав';
  }

  const upButtons = [];
  if (canCreate && !errorMessage) {
    upButtons.push({
      title: 'Создать',
      onClick: () => navigate(`${itemDir}/new`),
      type: 'icon',
      icon: <AddIcon />,
      xs: 2,
      position: 'left',
    });
  }

  if (canDelete && !errorMessage) {
    upButtons.push({
      title: 'Показать скрытые',
      onClick: () => {
        setViewInvisible(!viewInvisible);
      },
      type: 'icon',
      icon: viewInvisible ? (
        <VisibilityOffIcon color='warning' />
      ) : (
        <VisibilityIcon />
      ),
      xs: 2,
      position: 'left',
    });
  }

  upButtons.push({
    title: 'Поиск',
    onClick: () => {
      if (showSearchBar) {
        setShowSearchBar(false);
        setSearchParam('');
        setSearchValue('');
      } else {
        setShowSearchBar(true);
      }
    },
    type: 'icon',
    icon: showSearchBar ? (
      <SearchOffIcon color='success' />
    ) : (
      <SearchIcon color='inherit' />
    ),
    xs: 2,
    position: 'left',
  });

  upButtons.push({
    title: 'Закрыть',
    onClick: () => {
      navigate('/');
    },
    type: 'icon',
    icon: <CloseIcon />,
    xs: 2,
    position: 'right',
  });

  const handleChangePage = (event, nextPage) => {
    if (pagination?.url) navigate(`${pagination.url}?page=${nextPage}`);
  };

  const handleChangeFilter = (key, value) => {
    setFilterInputs((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddDrawerSelect = (target, value) => {
    setFilterInputs((prev) => ({ ...prev, [target]: value }));
    setAddDrawerOptions({ open: false });
  };

  if (errorMessage) {
    return (
      <ScreenContainer buttons={upButtons} isProgress={isFetching}>
        <Alert severity='error' sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </ScreenContainer>
    );
  }

  const hasFilter =
    Array.isArray(data?.metadata?.filters) &&
    data?.metadata?.filters.length > 0;

  return (
    <>
      <ScreenContainer
        buttons={upButtons}
        isProgress={isFetching}
        showSearchBar={showSearchBar}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSearch={handleSearch}
        searchIconStatus={!!searchParam ? 'clear' : 'search'}
      >
        <Card>
          <CardHeader
            title={data?.metadata?.title || 'Список'}
            // subheader={data?.metadata?.subtitle}
            action={
              hasFilter && (
                <IconButton
                  aria-label='settings'
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FilterAltIcon />
                </IconButton>
              )
            }
          />
          {hasFilter && showFilters && (
            <CardContent sx={{ pt: 0 }}>
              <Grid container sx={{ py: 1.5, px: 2 }} spacing={1}>
                {data?.metadata?.filters.map((filter, index) => {
                  return (
                    <Grid
                      item
                      xs={filter?.width || 12}
                      key={filter?.field || index}
                      sx={{
                        minWidth: `${(filter?.width / 12) * 100}%`,
                      }}
                    >
                      <FieldInput
                        field={filter}
                        value={
                          filterInputs[filter.field]?.title ||
                          filterInputs[filter.field]?.name ||
                          filterInputs[filter.field] ||
                          ''
                        }
                        onChange={handleChangeFilter} // connect to form
                        handleOpenDrawer={() =>
                          setAddDrawerOptions({
                            open: true,
                            target: filter.field,
                            multiple: filter.multiple,
                            ...(filter?.addOptions || {}),
                          })
                        }
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          )}
          {data?.rows?.length > 0 ? (
            <CardContent sx={{ pt: 0 }}>
              {data?.pagination?.totalPages > 1 && (
                <Pagination
                  count={data?.pagination?.totalPages}
                  page={data?.pagination?.page || 1}
                  shape='rounded'
                  onChange={handleChangePage}
                  sx={{ mb: 2 }}
                />
              )}
              <GridTableList
                sx={{ m: 0, p: 0 }}
                data={data}
                columns={columns.filter((col) => col?.visible !== false)}
                onRowSelect={handleRowSelect}
                selectedRowKey={navigateOptions?.itemKey}
                selectedId={anchorId}
              />
              {data?.pagination?.totalPages > 1 &&
                data?.pagination?.totalPages > 10 && (
                  <Pagination
                    count={data?.pagination?.totalPages}
                    page={data?.pagination?.page || 1}
                    shape='rounded'
                    onChange={handleChangePage}
                    sx={{ my: 2 }}
                  />
                )}
            </CardContent>
          ) : (
            <Typography textAlign='center' color='text.secondary' my={4}>
              Нет данных
            </Typography>
          )}
        </Card>
      </ScreenContainer>
      {addDrawerOptions?.open && (
        <AddDrawer
          open={addDrawerOptions?.open}
          onClose={() => setAddDrawerOptions(false)}
          title={`Подбор`}
          titleKey={addDrawerOptions?.titleKey}
          addSlug={addDrawerOptions.slug}
          filter={addDrawerOptions?.filter || []}
          existingIds={addDrawerOptions?.existingIds || []}
          onSelect={(data) => {
            // console.log('AddDrawer data', data);

            handleAddDrawerSelect(addDrawerOptions?.target, data);
          }}
        />
      )}
      <Alerton message={message} handleCloseMessage={() => setMessage('')} />
    </>
  );
};
export default ItemsViewer;
